import {
  Show,
  createSignal,
  Suspense,
  Switch,
  Match,
  ErrorBoundary,
} from "solid-js";
import {
  createAsync,
  type RouteDefinition,
  type RouteSectionProps,
  useSearchParams,
  useAction,
} from "@solidjs/router";
import { Meta } from "@solidjs/meta";
import { useProduct } from "~/services/products/useProduct";
import { useSessionContext, useSiteContext } from "~/utils/contexts";
import { photoFrameSizes, pfSizesByCode } from "~/utils/builtFrames";
import {
  ProductHeader,
  SwatchDisplay,
  PFGFPricingBox,
  FrameCounter,
  ProductDescription,
  ProductDetails,
  BuiltFrameCareInstr,
} from "~/components/product/product-page";
import { BaseSkeleton } from "~/components/utility";
import { ProductPageButtons } from "../[sku]";
import { Icon } from "solid-heroicons";
import { check } from "solid-heroicons/outline";
import { PT } from "~/utils/products";
import { EventType } from "@solid-primitives/analytics";
import { framePayloadGenerator } from "~/utils/builtFrames";
import { addToCartAction } from "~/services/cart";

export const route = {
  preload: ({ params }) =>
    useProduct(params.sku, {
      type: PT.PHOTOFRAME,
      available_list: true,
      suggestions: true,
    }),
} satisfies RouteDefinition;

export default function PhotoFrame(props: RouteSectionProps) {
  const { isPartner, permission, aCartActionIsPending } = useSessionContext();
  const { global, track, trackFBQ, setPanel } = useSiteContext();
  const [params, _] = useSearchParams<{ size: string }>();
  const [qty, setQty] = createSignal(2);
  const [pricing, setPricing] = createSignal();
  const status = {
    READY: 0,
    LOADING: 1,
    ADDED: 2,
    ERROR: 3,
  };
  const [addCartStatus, setAddCartStatus] = createSignal(status.READY);
  const product = createAsync(() =>
    useProduct(props.params.sku, {
      type: PT.PHOTOFRAME,
      available_list: true,
      suggestions: true,
    })
  );

  const size = () => {
    if (isPartner() && !params.size) {
      return photoFrameSizes[0].code;
    } else if (!params.size) {
      // ?why?
      return "46";
    }
    return params.size;
  };

  // const frameSizeOptions = Object.entries(pfSizesByCode).map(([code, data]) => {
  //   return {
  //     value: code,
  //     label: (
  //       <span>
  //         {data.size[0]}
  //         <span class="text-xs mx-1">&#10005;</span>
  //         {data.size[1]}
  //       </span>
  //     ),
  //   };
  // });
  const frameSizeOptions = Object.entries(pfSizesByCode).map(([code, data]) => {
    return {
      value: code,
      label: `${data.size[0]} x ${data.size[1]}`,
      width: data.size[0],
      height: data.size[1],
    };
  });

  const addToCart = useAction(addToCartAction);

  const addFrameToCart = async (payload: any) => {
    const [item] = payload;
    setAddCartStatus(status.LOADING);
    try {
      await addToCart(item);
      setAddCartStatus(status.ADDED);
      setPanel({ mode: "cart", open: true });
    } catch (error) {
      console.log("ERROR ADDING PHOTOFRAME TO CART : ", error);
      setAddCartStatus(status.ERROR);
    }
  };

  return (
    <>
      <Show when={product() && global()}>
        <div class="!border-t-0">
          <Meta name="Algolia crawler" content="noindex" />
          <ProductHeader
            product={product}
            type={PT.PHOTOFRAME}
            size={params.size}
          />
          <ErrorBoundary fallback={(error,reset)=>{
            return (
              <div class="bg-red-100 rounded-md p-3 text-sm">
              <p>Error retrieving pricing information. Please try again later, we apologize for the inconvenience.</p>
              {/* TODO - sentry  */}
            </div>
            )
          }}>
            <Suspense fallback={<BaseSkeleton height={465} />}>
              <Show
                when={isPartner()}
                fallback={
                  <SwatchDisplay product={product} type={PT.PHOTOFRAME} />
                }
              >
                <PFGFPricingBox
                  product={product}
                  sizes={photoFrameSizes}
                  sizeOptions={frameSizeOptions}
                  activeSize={size}
                  sku={props.params.sku}
                  productType={PT.PHOTOFRAME}
                  setPricing={setPricing}
                />
                <Show when={permission.PLACEORDER}>
                  <div class="grid grid-cols-2 sm:grid-cols-3 gap-x-2">
                    <FrameCounter
                      minimum={2}
                      getCount={qty}
                      updateCount={setQty}
                      label="Quantity"
                    />
                  </div>
                </Show>
              </Show>
              <ProductPageButtons
                hideBuyButton={product()?.ComingSoon}
                product={product()!}
                buttonText={
                  <>
                    <Switch>
                      <Match when={addCartStatus() === status.READY}>
                        Add to Cart
                      </Match>
                      <Match when={addCartStatus() === status.ADDED}>
                        <span class="flex items-center">
                          Added
                          <Icon
                            path={check}
                            class="w-4 inline-block ml-2"
                            stroke-width={3}
                          />
                        </span>
                      </Match>
                      <Match when={addCartStatus() === status.LOADING}>
                        Adding...
                      </Match>
                      <Match when={addCartStatus() === status.ERROR}>
                        Error
                      </Match>
                    </Switch>
                  </>
                }
                disabled={aCartActionIsPending()}
                onOrderClick={() => {
                  // TODO - extract track/trackFBQ to util fn for cleanliness (comps/products/product-page/utils)
                  track(EventType.Event, {
                    category: "add_to_cart",
                    action: "PF Added",
                    value: (pricing() as any)?.[size()].Amount,
                    // @ts-expect-error
                    currency: (pricing() as any)?.[size()]?.Currency ?? "",
                    items: [
                      {
                        item_id: `${product()?.SKU}-${size()}`,
                        item_name: product()?.Name,
                        item_category: PT.PHOTOFRAME,
                        item_category2: product()?.Category,
                        item_category3: product()?.Collection,
                        quantity: qty(),
                        price: (pricing() as any)?.[size()]?.Amount ?? "",
                      },
                    ],
                  });
                  trackFBQ("AddToCart", {
                    content_category: PT.PHOTOFRAME,
                    content_name: product.latest?.Name,
                    content_ids: [`${product.latest?.SKU}-${size()}`],
                    contents: [
                      { id: `${product.latest?.SKU}-${size()}`, quantity: qty() },
                    ],
                    currency: (pricing() as any)?.[size()].Currency ?? "",
                    value: (pricing() as any)?.[size()]?.Amount ?? "",
                  });
                  addFrameToCart(
                    framePayloadGenerator(
                      props.params.sku,
                      [size()],
                      PT.PHOTOFRAME,
                      qty()
                    )
                  );
                }}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
        <ProductDescription description={product()?.Description as string} />
        <ProductDetails product={product} hideAvailableAs>
          <ul class="list-inside list-disc pb-3 text-sm text-roma-dark-grey pl-2">
            <li>Scratch Resistant Glass</li>
            <li>Durable easel backing</li>
            <li>Landscape or portrait display</li>
            <li>Custom made with love and care</li>
            <li>Solid wood frame made in Italy</li>
            <li>Assembled in the USA and Canada</li>
            <li>Durable and scratch resistant finish</li>
            <li>100% Happiness Guarantee</li>
          </ul>
        </ProductDetails>
        <BuiltFrameCareInstr />
      </Show>
    </>
  );
}
